import { CardDef, DeckDef } from '../types.js';
import { PlayerData, getActiveDeck, getDeckCardCount } from '../collection/PlayerState.js';
import type { PackDef } from '../shared/api-types.js';
import type { TournamentSummary } from '../shared/tournament-types.js';
import type { AuctionMenuSummary } from '../shared/auction-types.js';
import {
  renderTournamentDeckPicker,
  setTournamentDeckId,
} from '../collection/TournamentDeckPrefs.js';
import { getEffectiveEntryCost, CUSTOM_PACK_TICKET_ID, type CustomPackConfig } from '../shared/cardgen.js';
import { formatCountdown } from './Countdown.js';

export type MenuAction =
  | { type: 'play' }
  | { type: 'edit-deck'; deckIndex: number }
  | { type: 'new-deck' }
  | { type: 'buy-deck-slot' }
  | { type: 'set-active-deck'; deckIndex: number }
  | { type: 'practice'; deckIndex: number }
  | { type: 'buy-pack'; packDef: PackDef }
  | { type: 'buy-custom-pack' }
  | { type: 'choose-starter'; deck: DeckDef }
  | { type: 'sign-in' }
  | { type: 'register' }
  | { type: 'sign-out' }
  | { type: 'view-campaigns' }
  | { type: 'view-campaign'; campaignId: string }
  | { type: 'play-stage'; campaignId: string; stageId: string }
  | { type: 'view-settings' }
  | { type: 'view-tournaments' }
  | { type: 'view-auction-house' }
  | { type: 'view-tournament'; tournamentId: string }
  | { type: 'play-tournament'; tournamentId: string }
  | { type: 'claim-tournament-prize'; tournamentId: string }
  | { type: 'back-tournament-detail' }
  | { type: 'back-tournament-ladder'; tournamentId: string }
  | { type: 'back-to-menu' }
  | { type: 'open-store' }
  | { type: 'claim-daily-gems' };

export function createStarterSelectionView(
  container: HTMLElement,
  starterDecks: DeckDef[],
  catalog: Map<string, CardDef>,
  onAction: (action: MenuAction) => void,
  isGuest: boolean,
): void {
  container.innerHTML = '';
  const el = document.createElement('div');
  el.className = 'menu-screen starter-selection';

  el.innerHTML = `
    <div class="account-bar">
      <span class="account-sign-in-link" id="starter-sign-in">Already have an account? Sign In</span>
    </div>
    <h1 class="menu-title">Welcome to Cardverse</h1>
    <p class="menu-subtitle">Choose your starter deck</p>
    <div class="starter-deck-grid"></div>
  `;

  el.querySelector('#starter-sign-in')!.addEventListener('click', () => {
    onAction({ type: 'sign-in' });
  });

  const grid = el.querySelector('.starter-deck-grid')!;
  for (const deck of starterDecks) {
    const card = document.createElement('div');
    card.className = 'starter-deck-card';

    // Count rarities
    const rarityCounts: Record<string, number> = {};
    for (const entry of deck.cards) {
      const def = catalog.get(entry.id);
      if (def) {
        rarityCounts[def.rarity] = (rarityCounts[def.rarity] ?? 0) + entry.count;
      }
    }

    // Show a few key cards
    const keyCards = deck.cards
      .filter(c => {
        const d = catalog.get(c.id);
        return d && d.type === 'character' && (d.rarity === 'legendary' || d.rarity === 'rare');
      })
      .slice(0, 4)
      .map(c => catalog.get(c.id)!.name)
      .join(', ');

    card.innerHTML = `
      <h2 class="starter-deck-name">${deck.name}</h2>
      <div class="starter-deck-info">
        <span>${getDeckCardCount(deck)} cards</span>
      </div>
      <div class="starter-deck-rarity">
        ${rarityCounts.legendary ? `<span class="rarity-tag legendary">${rarityCounts.legendary} Legendary</span>` : ''}
        ${rarityCounts.rare ? `<span class="rarity-tag rare">${rarityCounts.rare} Rare</span>` : ''}
        ${rarityCounts.uncommon ? `<span class="rarity-tag uncommon">${rarityCounts.uncommon} Uncommon</span>` : ''}
        ${rarityCounts.common ? `<span class="rarity-tag common">${rarityCounts.common} Common</span>` : ''}
      </div>
      <div class="starter-deck-key-cards">Featured: ${keyCards}</div>
      <button class="menu-btn primary">Choose This Deck</button>
    `;

    card.querySelector('button')!.addEventListener('click', () => {
      onAction({ type: 'choose-starter', deck });
    });

    grid.appendChild(card);
  }

  container.appendChild(el);
}

export function createMainMenuView(
  container: HTMLElement,
  playerData: PlayerData,
  catalog: Map<string, CardDef>,
  packs: PackDef[],
  maxDecks: number,
  onAction: (action: MenuAction) => void,
  isGuest: boolean,
  username?: string,
  customPack?: CustomPackConfig,
  tournaments: TournamentSummary[] = [],
  // Derived, data-driven menu gates (computed in main.ts from the
  // rules bundle + player progress):
  //   campaignHasIncomplete — show a red pip on the Campaign button to
  //     nudge the player toward unfinished, currently-available stages.
  //   tournamentsUnlocked — whether the player has cleared the
  //     progression gate (data/progression.json). When false, both the
  //     Tournaments button and its at-a-glance widget stay hidden.
  campaignHasIncomplete: boolean = false,
  tournamentsUnlocked: boolean = true,
  // Gem-cost schedule for purchasable extra deck slots. `costs[i]` is
  // the cost of the (i+1)-th extra slot; length is the hard cap.
  // Drives the "buy slot" affordance shown when the player is at
  // their current deck-list cap.
  deckSlotCosts: number[] = [],
  // Auction house gate + at-a-glance summary.
  //   auctionHouseUnlocked — cleared the campaign gate
  //     (data/progression.json:auctionHouseUnlockStageIds).
  //   auctionSummary — { activeBids, pendingClaims, activeSales,
  //     unsoldSales, unreadNotifications }; drives the tile's
  //     secondary line + the red pip. Null while loading or when
  //     the endpoint is unreachable — the tile renders without a pip.
  //   The tile hides entirely for guests since the auction house is
  //     signed-in only.
  auctionHouseUnlocked: boolean = false,
  auctionSummary: AuctionMenuSummary | null = null,
): void {
  container.innerHTML = '';
  const el = document.createElement('div');
  el.className = 'menu-screen main-menu';

  // Tournaments only surface once unlocked AND at least one exists.
  const showTournaments = tournamentsUnlocked && tournaments.length > 0;
  // Auction house: signed-in-only + campaign gate. Guests see nothing
  // (unlike Tournaments which shows the tile for guests as a teaser)
  // because unlike tournaments, auctions have no meaningful preview
  // for a guest — the entire flow requires an account.
  const showAuctionHouse = auctionHouseUnlocked && !isGuest;
  const auctionPipCount = auctionSummary?.unreadNotifications ?? 0;

  const activeDeck = getActiveDeck(playerData);
  const activeDeckCount = activeDeck ? getDeckCardCount(activeDeck) : 0;

  const accountBarHtml = isGuest
    ? `<div class="account-bar">
        <span class="account-status">Signed in as Guest</span>
        <span class="account-link" id="menu-sign-in">Sign In</span>
        <span class="account-link" id="menu-register">Create Account</span>
      </div>`
    : `<div class="account-bar">
        <span class="account-status">Signed in as <strong>${escapeHtml(username ?? '')}</strong></span>
        <span class="account-link" id="menu-settings">Settings</span>
        <span class="account-link" id="menu-sign-out">Sign Out</span>
      </div>`;

  el.innerHTML = `
    ${accountBarHtml}
    <h1 class="menu-title">Cardverse</h1>
    <div class="player-currencies">
      <span class="player-coins">${playerData.coins} coins</span>
      <span class="player-gems">${playerData.gems} gems</span>
      ${dailyGemsBlockHtml(playerData)}
      <button class="menu-btn-sm gem-shop-link" id="open-store">Get More</button>
    </div>
    <div class="menu-buttons">
      <button class="menu-btn primary" id="play-btn" ${activeDeckCount < 50 ? 'disabled' : ''}>Play</button>
      ${playerData.hasReceivedStarterPack
        ? `<button class="menu-btn primary-blue" id="campaign-btn" ${activeDeckCount < 50 ? 'disabled' : ''}>Campaign${campaignHasIncomplete ? '<span class="menu-pip" aria-label="Incomplete campaign stages"></span>' : ''}</button>`
        : ''}
      ${showTournaments
        ? `<button class="menu-btn primary-purple" id="tournaments-btn" ${activeDeckCount < 50 ? 'disabled' : ''}>Tournaments</button>`
        : ''}
      ${showAuctionHouse
        ? `<button class="menu-btn primary-gold auction-house-btn" id="auction-house-btn">
             <span class="menu-btn-label">Auction</span>
             ${renderAuctionHouseSubtitle(auctionSummary)}
             ${auctionPipCount > 0 ? '<span class="menu-pip" aria-label="Unread auction notifications"></span>' : ''}
           </button>`
        : ''}
    </div>
    ${showTournaments ? renderActiveTournamentWidget(tournaments, playerData) : ''}
    <div class="menu-content">
      <div class="deck-manager">
        <h2 class="section-title">Your Decks</h2>
        <div class="deck-list-menu" id="deck-list-menu"></div>
        ${renderDeckSlotControl(playerData, maxDecks, deckSlotCosts)}
      </div>
      <div class="pack-shop">
        <h2 class="section-title">Card Shop</h2>
        <div class="pack-grid"></div>
      </div>
    </div>
  `;

  el.querySelector('#play-btn')!.addEventListener('click', () => onAction({ type: 'play' }));
  el.querySelector('#campaign-btn')?.addEventListener('click', () => onAction({ type: 'view-campaigns' }));
  el.querySelector('#tournaments-btn')?.addEventListener('click', () => onAction({ type: 'view-tournaments' }));
  el.querySelector('#auction-house-btn')?.addEventListener('click', () => onAction({ type: 'view-auction-house' }));
  el.querySelector('#open-store')?.addEventListener('click', () => onAction({ type: 'open-store' }));
  el.querySelector('#claim-daily-gems')?.addEventListener('click', () => onAction({ type: 'claim-daily-gems' }));

  el.querySelector('.tournament-widget [data-action="play-tournament"]')?.addEventListener('click', () => {
    const id = el.querySelector('.tournament-widget')!.getAttribute('data-tournament-id')!;
    onAction({ type: 'play-tournament', tournamentId: id });
  });
  const widgetDeckSelect = el.querySelector<HTMLSelectElement>('.tournament-widget .tournament-deck-picker select');
  if (widgetDeckSelect) {
    // The widget shows whichever tournament `renderActiveTournamentWidget`
    // picked first; that pick's defId lives on the rendered TournamentSummary,
    // so look it up via the data-tournament-id attribute to recover it.
    const widgetTournamentId = el.querySelector('.tournament-widget')!.getAttribute('data-tournament-id');
    const widgetTournament = tournaments.find(t => t.id === widgetTournamentId);
    if (widgetTournament) {
      widgetDeckSelect.addEventListener('change', () => {
        setTournamentDeckId(widgetTournament.defId, widgetDeckSelect.value);
      });
    }
  }
  el.querySelector('.tournament-widget [data-action="claim-tournament"]')?.addEventListener('click', () => {
    const id = el.querySelector('.tournament-widget')!.getAttribute('data-tournament-id')!;
    onAction({ type: 'claim-tournament-prize', tournamentId: id });
  });
  el.querySelector('.tournament-widget [data-action="view-tournament"]')?.addEventListener('click', () => {
    const id = el.querySelector('.tournament-widget')!.getAttribute('data-tournament-id')!;
    onAction({ type: 'view-tournament', tournamentId: id });
  });

  const newDeckBtn = el.querySelector('#new-deck-btn');
  if (newDeckBtn) {
    newDeckBtn.addEventListener('click', () => onAction({ type: 'new-deck' }));
  }
  const buySlotBtn = el.querySelector('#buy-slot-btn');
  if (buySlotBtn) {
    buySlotBtn.addEventListener('click', () => onAction({ type: 'buy-deck-slot' }));
  }

  // Render deck list
  const deckListEl = el.querySelector('#deck-list-menu')!;
  for (let i = 0; i < playerData.decks.length; i++) {
    const deck = playerData.decks[i];
    const count = getDeckCardCount(deck);
    const isActive = i === playerData.activeDeckIndex;

    const deckEl = document.createElement('div');
    deckEl.className = `deck-list-item ${isActive ? 'active' : ''}`;
    deckEl.innerHTML = `
      <div class="dli-info">
        <span class="dli-name">${deck.name}</span>
        <span class="dli-count ${count === 50 ? '' : 'incomplete'}">${count}/50</span>
        ${isActive ? '<span class="dli-active-badge">Active</span>' : ''}
      </div>
      <div class="dli-actions">
        <button class="menu-btn dli-btn" data-action="practice" ${count < 50 || activeDeckCount < 50 ? 'disabled' : ''} title="${count < 50 || activeDeckCount < 50 ? 'Both your active deck and the practice opponent must be 50 cards' : 'Practice your active deck against this one'}">Practice</button>
        ${!isActive ? '<button class="menu-btn dli-btn" data-action="activate">Set Active</button>' : ''}
        <button class="menu-btn dli-btn" data-action="edit">Edit</button>
      </div>
    `;

    deckEl.querySelector('[data-action="activate"]')?.addEventListener('click', () => {
      onAction({ type: 'set-active-deck', deckIndex: i });
    });
    deckEl.querySelector('[data-action="edit"]')!.addEventListener('click', () => {
      onAction({ type: 'edit-deck', deckIndex: i });
    });
    deckEl.querySelector('[data-action="practice"]')!.addEventListener('click', () => {
      onAction({ type: 'practice', deckIndex: i });
    });

    deckListEl.appendChild(deckEl);
  }

  // Render pack shop
  const packGrid = el.querySelector('.pack-grid')!;
  for (const pack of packs) {
    const packEl = document.createElement('div');
    // A pack with a matching ticket in the player's inventory enters
    // ticket-redemption mode: the tile gains a FREE ribbon and the
    // button label flips. Coin balance becomes irrelevant for the
    // canAfford gate when a ticket applies.
    const ticketCount = pack.ticketId ? (playerData.tickets[pack.ticketId]?.count ?? 0) : 0;
    const hasTicket = ticketCount > 0;
    packEl.className = 'pack-card' + (hasTicket ? ' has-ticket' : '');
    const canAfford = hasTicket || playerData.coins >= pack.cost;
    const buttonLabel = hasTicket ? 'FREE' : `${pack.cost} coins`;
    packEl.innerHTML = `
      <div class="pack-details">
        <div class="pack-name">${pack.name}</div>
        <div class="pack-info">${pack.cardCount} cards</div>
      </div>
      <button class="menu-btn pack-buy-btn">
        ${buttonLabel}
      </button>
    `;
    // Click is never gated by affordability — players short on coins
    // get routed to the store, so the path "I want this pack" → "now
    // I have what I need" is one click instead of two screens of
    // hunting. canAfford only branches what the click does.
    packEl.querySelector('button')!.addEventListener('click', () => {
      if (canAfford) onAction({ type: 'buy-pack', packDef: pack });
      else onAction({ type: 'open-store' });
    });
    packGrid.appendChild(packEl);
  }

  // Custom pack tile. Mirrors the regular pack-card's ticket flow:
  // when the player holds at least one CUSTOM_PACK_TICKET_ID ticket,
  // the tile flips to FREE-redemption mode (button reads FREE, the
  // .has-ticket modifier paints the green border + ribbon, and
  // clicking redeems the ticket instead of charging gems).
  if (customPack) {
    const effectiveCost = getEffectiveEntryCost(customPack);
    const customTicketCount = playerData.tickets[CUSTOM_PACK_TICKET_ID]?.count ?? 0;
    const hasCustomTicket = customTicketCount > 0;
    const canAfford = hasCustomTicket || playerData.gems >= effectiveCost;
    const discount = customPack.discount;
    const packEl = document.createElement('div');
    packEl.className = 'pack-card custom-pack-tile' + (hasCustomTicket ? ' has-ticket' : '');
    const buttonLabel = hasCustomTicket
      ? 'FREE'
      : (discount
          ? `<span class="pack-buy-strike">${customPack.entryCost}</span> ${effectiveCost} gems`
          : `${effectiveCost} gems`);
    packEl.innerHTML = `
      <div class="pack-details">
        <div class="pack-name">Custom Pack</div>
        <div class="pack-info">Pick one of ${customPack.cardsPerPack} unique AI-generated cards</div>
        <div class="custom-pack-badge">AI</div>
      </div>
      <button class="menu-btn pack-buy-btn">
        ${buttonLabel}
      </button>
      ${!hasCustomTicket && discount ? `<div class="custom-pack-discount-banner">${escapeHtml(discount.label)}</div>` : ''}
    `;
    packEl.querySelector('button')!.addEventListener('click', () => {
      if (canAfford) onAction({ type: 'buy-custom-pack' });
      else onAction({ type: 'open-store' });
    });
    packGrid.appendChild(packEl);
  }

  // Account actions
  el.querySelector('#menu-sign-in')?.addEventListener('click', () => onAction({ type: 'sign-in' }));
  el.querySelector('#menu-register')?.addEventListener('click', () => onAction({ type: 'register' }));
  el.querySelector('#menu-sign-out')?.addEventListener('click', () => onAction({ type: 'sign-out' }));
  el.querySelector('#menu-settings')?.addEventListener('click', () => onAction({ type: 'view-settings' }));

  container.appendChild(el);
}

/** Returns the markup for the affordance below the deck list:
 *
 *  - Player below cap → "+ New Deck" (creates a deck immediately).
 *  - Player at cap, more slots available on the schedule → "+ Deck Slot (N gems)"
 *    (gated by gem balance: disabled with tooltip if too poor).
 *  - Player at cap, schedule exhausted → no button (silent ceiling).
 *
 *  Returning the empty string in the third case keeps the layout
 *  clean for players who've maxed out (or for whom the schedule is
 *  empty in data). */
function renderDeckSlotControl(playerData: PlayerData, maxDecks: number, deckSlotCosts: number[]): string {
  const cap = maxDecks + playerData.extraDeckSlots;
  if (playerData.decks.length < cap) {
    return '<button class="menu-btn" id="new-deck-btn">+ New Deck</button>';
  }
  const nextCost = deckSlotCosts[playerData.extraDeckSlots];
  if (nextCost === undefined) return '';
  return `
    <button class="menu-btn buy-slot-btn" id="buy-slot-btn">
      <span class="buy-slot-label">+ Deck Slot</span>
      <span class="buy-slot-cost">${nextCost} gems</span>
    </button>
  `;
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/** Subtitle line rendered inside the Auction House button: `N bids ·
 *  M sales · K claim(s)`. Renders nothing (empty string) when there's
 *  nothing to report — the button then shows just its main label. When
 *  there ARE pending claims, they're highlighted since they're the
 *  highest-value affordance ("come pick up your card").
 *
 *  We DON'T surface `unsoldSales` — for unsold auctions the card was
 *  already returned to the seller's collection at close time and the
 *  `unsold` notification popup acknowledged the outcome. There's
 *  nothing left for the player to do about it, so a persistent count
 *  in the subtitle is just noise. The field stays in the DTO in case
 *  a future history view surfaces it, but the menu tile ignores it. */
function renderAuctionHouseSubtitle(summary: AuctionMenuSummary | null): string {
  if (!summary) return '';
  const parts: string[] = [];
  if (summary.activeBids > 0) parts.push(`${summary.activeBids} bid${summary.activeBids === 1 ? '' : 's'}`);
  if (summary.pendingClaims > 0) parts.push(`<strong>${summary.pendingClaims} to claim</strong>`);
  if (summary.activeSales > 0) parts.push(`${summary.activeSales} sale${summary.activeSales === 1 ? '' : 's'}`);
  if (parts.length === 0) return '';
  return `<span class="menu-btn-subtitle">${parts.join(' &middot; ')}</span>`;
}

/** Render the daily-gems claim button next to the gem balance. Shown
 *  only when the subscription is active AND today's claim hasn't been
 *  spent — the already-claimed state lives in the gem shop's
 *  subscription tile, so the main menu stays clutter-free once the
 *  daily reward has been collected. The leading sparkle is a
 *  decorative twinkle (animated via CSS) to draw the player's eye. */
/** "Most relevant" tournament widget on the main menu. Priority order:
 *
 *  1. Active tournament the player is entered in → show rank + Play.
 *  2. Ended tournament with an unclaimed prize → show Claim.
 *  3. Active tournament not yet entered → show "Join & Play" pitch.
 *
 *  No widget rendered when none of these apply. The Tournaments button
 *  in `.menu-buttons` is the always-on way into the list view; the
 *  widget is the at-a-glance summary so returning players don't have
 *  to dig. */
function renderActiveTournamentWidget(tournaments: TournamentSummary[], playerData: PlayerData): string {
  if (tournaments.length === 0) return '';

  const unclaimed = tournaments.find(t => t.unclaimedPrize);
  const enteredActive = tournaments.find(t => t.entered && t.isActive);
  const upcomingActive = tournaments.find(t => t.isActive && !t.entered);

  const pick = enteredActive ?? unclaimed ?? upcomingActive;
  if (!pick) return '';

  const countdown = pick.isEnded
    ? 'Ended'
    : formatCountdown(new Date(pick.endAt).getTime());

  const rankText =
    pick.entered && pick.playerRank != null
      ? `Rank #${pick.playerRank} of ${pick.totalEntries}`
      : `${pick.totalEntries} players`;

  // Layout (active match):
  //   row 1  [name]                    [Details] [countdown]
  //   bottom [rank]                    [Play Match]
  //          [deck picker]              (tall, spans both lines)
  //
  // Moving Details up to row 1 separates it from Play Match so a thumb
  // aiming for one doesn't catch the other. Pulling Play Match into a
  // single tall button on the right side of the bottom block also lets
  // us give it more padding without making the widget visibly taller.
  //
  // Claim-prize state collapses to two rows because there's no deck to
  // pick and no Details secondary CTA — just the headline action.
  if (pick === unclaimed) {
    return `
      <div class="tournament-widget" data-tournament-id="${pick.id}">
        <div class="tournament-widget-row">
          <span class="tournament-widget-name">${escapeHtml(pick.displayName)}</span>
          <span class="tournament-widget-countdown">${countdown}</span>
        </div>
        <div class="tournament-widget-row">
          <span class="tournament-widget-rank">${rankText}</span>
          <button class="menu-btn primary-purple" data-action="claim-tournament">Claim Prize</button>
        </div>
      </div>
    `;
  }

  const deckPicker = pick.isActive
    ? renderTournamentDeckPicker(pick.defId, playerData)
    : '';
  const playLabel = pick.entered ? 'Play Match' : 'Join &amp; Play';

  return `
    <div class="tournament-widget" data-tournament-id="${pick.id}">
      <div class="tournament-widget-row">
        <span class="tournament-widget-name">${escapeHtml(pick.displayName)}</span>
        <div class="tournament-widget-header-actions">
          <button class="menu-btn-sm" data-action="view-tournament">Details</button>
          <span class="tournament-widget-countdown">${countdown}</span>
        </div>
      </div>
      <div class="tournament-widget-bottom">
        <div class="tournament-widget-bottom-left">
          <span class="tournament-widget-rank">${rankText}</span>
          ${deckPicker}
        </div>
        <button class="menu-btn primary-purple tournament-widget-play-match" data-action="play-tournament">${playLabel}</button>
      </div>
    </div>
  `;
}

function dailyGemsBlockHtml(playerData: PlayerData): string {
  const ticket = playerData.tickets['daily-gems'];
  if (!ticket || ticket.count <= 0) return '';
  const canClaim = !ticket.lastUsed || (
    new Date(ticket.lastUsed).toISOString().slice(0, 10) <
    new Date().toISOString().slice(0, 10)
  );
  if (!canClaim) return '';
  return '<button class="menu-btn-sm daily-gems-btn" id="claim-daily-gems"><span class="daily-gems-sparkle">✨</span> Get Daily Gems</button>';
}
